/**
 * NotificationBell — realtime notification indicator with unread count and a
 * dropdown list.
 *
 * Behaviour contract:
 *  - The API/DB is authoritative. The unread count is fetched on mount and
 *    re-fetched on every socket (re)connect, so a notification that arrived
 *    while the browser was offline becomes visible without a full page reload.
 *  - Socket events only accelerate: each one is deduped by notification id, so a
 *    duplicate event cannot double-count or double-render.
 *  - Clicking a notification marks it read and navigates to its canonical
 *    destination (see utils/notificationDestination). Types with no legitimate
 *    destination stay non-actionable rather than linking somewhere wrong.
 *  - Sound plays only for a notification received live, only for the audible
 *    types, and never while hydrating history (see utils/notificationSound).
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCheck, X, Calendar, Stethoscope, CreditCard,
  FlaskConical, BarChart3, Pill, AlertTriangle, BellRing,
  ClipboardList, Volume2, VolumeX
} from 'lucide-react';
import { notificationsApi, connectSocket } from '../../services/api';
import type { Notification } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
import { formatDateTime, toArrayPayload } from '../../utils/formatters';
import { getNotificationDestination } from '../../utils/notificationDestination';
import {
  isAudibleNotificationType,
  isNotificationSoundEnabled,
  setNotificationSoundEnabled,
  playNotificationSound,
  unlockNotificationSound,
  primeNotificationSoundOnFirstGesture,
} from '../../utils/notificationSound';

const TYPE_ICONS: Record<string, React.ReactNode> = {
  booking_confirmation: <Calendar size={14} className="text-primary-500" />,
  doctor_assigned: <Stethoscope size={14} className="text-success-500" />,
  payment_completed: <CreditCard size={14} className="text-primary-500" />,
  test_recommended: <FlaskConical size={14} className="text-warning-600" />,
  results_available: <BarChart3 size={14} className="text-success-500" />,
  prescription_ready: <Pill size={14} className="text-primary-500" />,
  emergency_alert: <AlertTriangle size={14} className="text-danger-500" />,
  system_alert: <BellRing size={14} className="text-text-muted" />,
  nurse_assigned: <ClipboardList size={14} className="text-success-500" />,
  // A doctor declined the booking — the patient needs it to look different
  // from a routine confirmation.
  booking_declined: <X size={14} className="text-danger-500" />,
};

interface NotificationBellProps {
  userId: string;
  /** Authenticated role (from the session, never from a notification payload). */
  role?: string | null;
  onNotificationClick?: (notification: Notification) => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ userId, role, onNotificationClick }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => isNotificationSoundEnabled());
  const [soundBlocked, setSoundBlocked] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Ids already accounted for, so a duplicate socket event (or a realtime event
  // for a row we already fetched) can never double-count or repeat the sound.
  const seenIdsRef = useRef<Set<unknown>>(new Set());
  const isOpenRef = useRef(false);
  const listenStopRef = useRef<(() => void) | null>(null);

  isOpenRef.current = isOpen;

  useEffect(() => {
    listenStopRef.current = primeNotificationSoundOnFirstGesture();
    return () => { listenStopRef.current?.(); };
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationsApi.getUnreadCount();
      setUnreadCount(res.data.count);
    } catch {
      // Silently handle — the next load or reconnect retries
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await notificationsApi.getMyNotifications({ limit: 20 });
      const rows: Notification[] = toArrayPayload(res.data?.notifications);
      // Hydration must never make a sound, and these ids must not be treated as
      // "new" if the same notification also arrives over the socket.
      rows.forEach((n) => seenIdsRef.current.add(n.id));
      setNotifications(rows);
      if (typeof res.data.unreadCount === 'number') setUnreadCount(res.data.unreadCount);
    } catch {
      // Silently handle
    } finally {
      setLoading(false);
    }
  }, []);

  /** Authoritative resync — used on mount and on every socket reconnect. */
  const resync = useCallback(async () => {
    await fetchUnreadCount();
    if (isOpenRef.current) await loadNotifications();
  }, [fetchUnreadCount, loadNotifications]);

  useEffect(() => {
    resync();
  }, [resync]);

  useEffect(() => {
    let socket: any;
    let cancelled = false;

    const handleRealtime = (data: any) => {
      if (!data) return;
      const id = data.id;
      if (id != null && seenIdsRef.current.has(id)) return; // duplicate delivery
      if (id != null) seenIdsRef.current.add(id);

      setNotifications(prev => (prev.some(n => n.id === id) ? prev : [data, ...prev].slice(0, 20)));
      setUnreadCount(prev => prev + 1);

      // Sound only for a live notification, only for audible types.
      if (isAudibleNotificationType(data?.type)) {
        const played = playNotificationSound();
        if (!played && isNotificationSoundEnabled()) setSoundBlocked(true);
      }
    };

    connectSocket(userId)
      .then((s) => {
        if (cancelled) { try { s.disconnect(); } catch {} return; }
        socket = s;
        socket.on('notification', handleRealtime);
        // Socket.IO reconnects on its own; every (re)connect resynchronizes the
        // authoritative unread state so nothing is permanently missed.
        socket.on('connect', () => { resync(); });
      })
      .catch(() => {
        // Realtime unavailable (no token / offline). The API remains the source
        // of truth and the next load reflects everything.
      });

    return () => {
      cancelled = true;
      if (socket) {
        try { socket.off('notification', handleRealtime); } catch {}
        try { socket.disconnect(); } catch {}
      }
    };
  }, [userId, resync]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) loadNotifications();
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silently handle
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Silently handle
    }
  };

  const handleNotificationOpen = (notif: Notification) => {
    if (!notif.isRead) handleMarkAsRead(notif.id);
    onNotificationClick?.(notif);
    setIsOpen(false);
    const destination = getNotificationDestination(notif, role);
    if (destination) navigate(destination);
  };

  const handleToggleSound = () => {
    const next = !soundEnabled;
    setNotificationSoundEnabled(next);
    setSoundEnabled(next);
    if (next) {
      // A click is a user gesture — the one moment the browser lets us start audio.
      unlockNotificationSound();
      setSoundBlocked(false);
    }
  };

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* Bell Button */}
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-lg hover:bg-surface-100 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label={`${t('notificationBell.notifications')}${unreadCount > 0 ? `, ${unreadCount} ${t('notificationBell.unread')}` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Bell size={20} className="text-text-secondary" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center
            bg-danger-500 text-white text-[10px] font-bold rounded-full px-1"
            aria-hidden="true"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown — responsive width */}
      {isOpen && (
        <div className="absolute top-full right-0 z-50
          w-[calc(100vw-2rem)] sm:w-[360px] max-h-[480px]
          bg-white rounded-xl shadow-modal overflow-hidden
          animate-slide-up mt-2"
          role="menu"
          aria-label={t('notificationBell.notificationsList')}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200">
            <span className="text-base font-bold text-text-primary">{t('notificationBell.notifications')}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleSound}
                className="p-1 rounded hover:bg-surface-100 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
                aria-label={soundEnabled ? t('notificationBell.soundOn') : t('notificationBell.soundOff')}
                aria-pressed={soundEnabled}
                title={soundEnabled ? t('notificationBell.soundOn') : t('notificationBell.soundOff')}
              >
                {soundEnabled
                  ? <Volume2 size={15} className="text-primary-500" />
                  : <VolumeX size={15} className="text-text-muted" />}
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-xs font-medium text-primary-500 hover:text-primary-700 transition-colors p-1 min-h-[36px]"
                  aria-label={t('notificationBell.markAllAsRead')}
                >
                  <CheckCheck size={14} />
                  <span className="hidden sm:inline">{t('notificationBell.markAllRead')}</span>
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded hover:bg-surface-100 transition-colors sm:hidden min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={t('notificationBell.closeNotifications')}
              >
                <X size={16} className="text-text-muted" />
              </button>
            </div>
          </div>

          {soundEnabled && soundBlocked && (
            <p className="px-4 py-2 text-[11px] text-text-muted bg-warning-50 border-b border-surface-200">
              {t('notificationBell.soundBlocked')}
            </p>
          )}

          {/* Notification List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="py-8 text-center text-text-muted text-sm">{t('app.loading')}</div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center text-text-muted text-sm">
                <Bell size={24} className="mx-auto mb-2 opacity-40" />
                {t('notificationBell.empty')}
              </div>
            ) : (
              notifications.map((notif) => {
                const actionable = !!getNotificationDestination(notif, role);
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleNotificationOpen(notif)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left
                      border-b border-surface-100 transition-colors hover:bg-surface-50
                      ${notif.isRead ? 'bg-white' : 'bg-primary-50/30'}`}
                    role="menuitem"
                  >
                    <span className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full bg-surface-50 flex items-center justify-center" aria-hidden="true">
                      {TYPE_ICONS[notif.type] || <Bell size={14} className="text-text-muted" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-text-primary truncate">{notif.title}</div>
                      <div className="text-xs text-text-secondary mt-0.5 line-clamp-2">{notif.message}</div>
                      <div className="text-[11px] text-text-muted mt-1">
                        {formatDateTime(notif.createdAt, t('app.notAvailable'))}
                        {!actionable && <span className="ml-2">{t('notificationBell.informational')}</span>}
                      </div>
                    </div>
                    {!notif.isRead && (
                      <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-2" aria-label={t('notificationBell.unread')} />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer — the full inbox is a real surface, so link it rather than
              leaving the route orphaned. */}
          <button
            onClick={() => { setIsOpen(false); navigate('/notifications'); }}
            className="w-full px-4 py-3 text-center text-xs font-semibold text-primary-600 hover:bg-surface-50 border-t border-surface-200 min-h-[44px]"
          >
            {t('notificationBell.viewAll')}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
